// webapp/frontend/src/services/api.js
import api from '@/plugins/axios'

// Helper function for error handling
const handleApiError = (error, context) => {
  console.error(`API Error (${context}):`, {
    status: error.response?.status,
    message: error.message,
    data: error.response?.data,
    config: error.config
  })
  
  // Handle 401 errors by redirecting to login
  if (error.response?.status === 401) {
    // Clear token and redirect to login
    localStorage.removeItem('token')
    window.location.href = '/login'
    return
  }
  
  throw error
}

// Create API instance with base URL
const apiInstance = api.create({
  // Use environment-aware baseURL: localhost:8000 for development, relative URLs for production
  baseURL: process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' 
    ? 'http://localhost:8000' 
    : undefined,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add auth token and project ID interceptor
apiInstance.interceptors.request.use(request => {
  const token = localStorage.getItem('token');
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  
  const projectId = localStorage.getItem('currentProjectId');
  console.debug(`[API] Interceptor adding X-Project-ID: ${projectId} to ${request.url}`)
  if (projectId) {
      request.headers['X-Project-ID'] = projectId;
  }
  
  return request;
});

// Add a request interceptor for debugging
apiInstance.interceptors.request.use(request => {
  console.log('API Request:', {
    method: request.method.toUpperCase(),
    url: request.url,
    baseURL: request.baseURL,
    fullURL: request.baseURL + request.url,
    headers: request.headers,
    data: request.data
  });
  return request;
});

// Add a response interceptor for debugging
apiInstance.interceptors.response.use(
  response => {
    console.log('API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  error => {
    console.error('API Error:', {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
      data: error.response?.data,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Export an object with all API methods
export default {
  // Raw GET (returns full axios response)
  async rawGet(url, config) {
    return apiInstance.get(url, config)
  },

  // Snapshot helpers (raw to access ETag/status)
  async getSnapshotRaw(dataset, etag) {
    // dataset: 'dev-items' | 'subsystems' | 'actions' | 'users'
    const headers = {}
    if (etag) headers['If-None-Match'] = etag
    
    // Add cache-busting timestamp to prevent 304s from browser cache when project ID changes
    // This forces the request to reach the server (or at least bypass URL-based caches)
    const t = Date.now()
    return apiInstance.get(`/api/v1/snapshots/${dataset}?t=${t}`, { headers })
  },

  async getDiffRaw(dataset, sinceVersion) {
    // dataset: 'dev-items' | 'subsystems' | 'actions' | 'users'
    return apiInstance.get(`/api/v1/snapshots/diffs/${dataset}`, { params: { since: sinceVersion } })
  },
  // Requirements
  async getRequirements(params = {}) {
    try {
      const res = await apiInstance.get('/api/v1/requirements/', { params })
      return res.data
    } catch (e) { handleApiError(e, 'getRequirements') }
  },
  async getRequirementById(id) {
    try { const res = await apiInstance.get(`/api/v1/requirements/${id}`); return res.data } catch (e) { handleApiError(e, 'getRequirementById') }
  },
  async createRequirement(data) {
    try { const res = await apiInstance.post('/api/v1/requirements/', data); return res.data } catch (e) { handleApiError(e, 'createRequirement') }
  },
  async updateRequirement(id, data) {
    try { const res = await apiInstance.put(`/api/v1/requirements/${id}`, data); return res.data } catch (e) { handleApiError(e, 'updateRequirement') }
  },
  async getDevItemRequirements(devItemId) {
    try { const res = await apiInstance.get(`/api/v1/dev-items/${devItemId}/requirements`); return res.data } catch (e) { handleApiError(e, 'getDevItemRequirements') }
  },
  async setDevItemRequirements(devItemId, requirementIds) {
    try { const res = await apiInstance.put(`/api/v1/dev-items/${devItemId}/requirements`, { requirement_ids: requirementIds }); return res.data } catch (e) { handleApiError(e, 'setDevItemRequirements') }
  },
  async attachDevItemRequirement(devItemId, requirementId) {
    try { await apiInstance.post(`/api/v1/dev-items/${devItemId}/requirements/${requirementId}`) } catch (e) { handleApiError(e, 'attachDevItemRequirement') }
  },
  async detachDevItemRequirement(devItemId, requirementId) {
    try { await apiInstance.delete(`/api/v1/dev-items/${devItemId}/requirements/${requirementId}`) } catch (e) { handleApiError(e, 'detachDevItemRequirement') }
  },
  async getSubsystemRequirements(subsystemId) {
    try { const res = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/requirements`); return res.data } catch (e) { handleApiError(e, 'getSubsystemRequirements') }
  },
  async setSubsystemRequirements(subsystemId, requirementIds) {
    try { const res = await apiInstance.put(`/api/v1/subsystems/${subsystemId}/requirements`, { requirement_ids: requirementIds }); return res.data } catch (e) { handleApiError(e, 'setSubsystemRequirements') }
  },
  async attachSubsystemRequirement(subsystemId, requirementId) {
    try { await apiInstance.post(`/api/v1/subsystems/${subsystemId}/requirements/${requirementId}`) } catch (e) { handleApiError(e, 'attachSubsystemRequirement') }
  },
  async detachSubsystemRequirement(subsystemId, requirementId) {
    try { await apiInstance.delete(`/api/v1/subsystems/${subsystemId}/requirements/${requirementId}`) } catch (e) { handleApiError(e, 'detachSubsystemRequirement') }
  },
  // Auth endpoints
  async login(username, password) {
    try {
      const params = new URLSearchParams()
      params.append('username', username)
      params.append('password', password)
      
      const response = await apiInstance.post('/api/v1/auth/token', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'login')
    }
  },

  // Dev Item assignment primary helpers
  async setDevItemPrimary(devItemId, userId) {
    try {
      const response = await apiInstance.post(`/api/v1/assignments/item/${devItemId}/primary/${userId}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'setDevItemPrimary')
    }
  },

  async addDevItemAssignment(userId, devItemId, isPrimary = false) {
    try {
      const response = await apiInstance.post(`/api/v1/assignments/user/${userId}/item/${devItemId}`, null, { params: { is_primary: isPrimary }})
      return response.data
    } catch (error) {
      handleApiError(error, 'addDevItemAssignment')
    }
  },

  // User endpoints
  async getCurrentUser() {
    try {
      const response = await apiInstance.get('/api/v1/users/me')
      return response.data
    } catch (error) {
      handleApiError(error, 'getCurrentUser')
    }
  },

  async getUsers() {
    try {
      const response = await apiInstance.get('/api/v1/users/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getUsers')
    }
  },

  async getUserById(id) {
    try {
      const response = await apiInstance.get(`/api/v1/users/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getUserById')
    }
  },

  async getUserNetwork(params = {}) {
    try {
      const response = await apiInstance.get('/api/v1/users/network', { params })
      return response.data
    } catch (error) {
      handleApiError(error, 'getUserNetwork')
    }
  },

  // System endpoints
  async getSystems() {
    try {
      const response = await apiInstance.get('/api/v1/systems/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getSystems')
    }
  },

  async getSystemById(id) {
    try {
      const response = await apiInstance.get(`/api/v1/systems/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getSystemById')
    }
  },

  async getSystemByName(name) {
    try {
      const res = await apiInstance.get(`/api/v1/systems/name/${encodeURIComponent(name)}`)
      return res.data
    } catch (e) { handleApiError(e, 'getSystemByName') }
  },

  // Subsystems endpoints
  async getSubsystems() {
    try {
      const response = await apiInstance.get('/api/v1/subsystems/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystems')
    }
  },
  // Categories
  async getCategories(){
    try { const res = await apiInstance.get('/api/v1/categories/'); return res.data } catch(e){ handleApiError(e,'getCategories') }
  },
  async createCategory({ name }){
    try { const res = await apiInstance.post('/api/v1/categories/', { name }); return res.data } catch(e){ handleApiError(e,'createCategory') }
  },
  async updateCategory(id, { name }){
    try { const res = await apiInstance.put(`/api/v1/categories/${id}`, { name }); return res.data } catch(e){ handleApiError(e,'updateCategory') }
  },
  async deleteCategory(id){
    try { await apiInstance.delete(`/api/v1/categories/${id}`) } catch(e){ handleApiError(e,'deleteCategory') }
  },
  // Subsystem categories
  async getSubsystemCategories(subsystemId){
    try { const res = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/categories`); return res.data } catch(e){ handleApiError(e,'getSubsystemCategories') }
  },
  async setSubsystemCategories(subsystemId, categoryIds){
    try { const res = await apiInstance.put(`/api/v1/subsystems/${subsystemId}/categories`, { category_ids: categoryIds }); return res.data } catch(e){ handleApiError(e,'setSubsystemCategories') }
  },
  async attachSubsystemCategory(subsystemId, categoryId){
    try { await apiInstance.post(`/api/v1/subsystems/${subsystemId}/categories/${categoryId}`) } catch(e){ handleApiError(e,'attachSubsystemCategory') }
  },
  async detachSubsystemCategory(subsystemId, categoryId){
    try { await apiInstance.delete(`/api/v1/subsystems/${subsystemId}/categories/${categoryId}`) } catch(e){ handleApiError(e,'detachSubsystemCategory') }
  },

  async getSubsystemById(id) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemById')
    }
  },

  async getSubsystemByName(name) {
    try {
      const res = await apiInstance.get(`/api/v1/subsystems/name/${encodeURIComponent(name)}`)
      return res.data
    } catch (e) { handleApiError(e, 'getSubsystemByName') }
  },

  // Subsystems tree endpoints
  async getSubsystemTree(params = {}) {
    try {
      const response = await apiInstance.get('/api/v1/subsystems/tree', { params })
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemTree')
    }
  },

  async getSubsystemChildren(subsystemId, params = {}) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/children`, { params })
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemChildren')
    }
  },

  async getSubsystemImplicitAssignees(subsystemId) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/implicit-assignees`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemImplicitAssignees')
    }
  },

  // Subsystem assignments endpoints
  async getSubsystemAssignments(subsystemId) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/assignments`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemAssignments')
    }
  },

  async setSubsystemAssignments(subsystemId, assignedUsers) {
    try {
      const response = await apiInstance.put(`/api/v1/subsystems/${subsystemId}/assignments`, assignedUsers)
      return response.data
    } catch (error) {
      handleApiError(error, 'setSubsystemAssignments')
    }
  },

  async addSubsystemAssignment(subsystemId, userId, isPrimary = false) {
    try {
      const response = await apiInstance.post(`/api/v1/subsystems/${subsystemId}/assignments/${userId}`, null, { params: { is_primary: isPrimary } })
      return response.data
    } catch (error) {
      handleApiError(error, 'addSubsystemAssignment')
    }
  },

  async removeSubsystemAssignment(subsystemId, userId) {
    try {
      const response = await apiInstance.delete(`/api/v1/subsystems/${subsystemId}/assignments/${userId}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'removeSubsystemAssignment')
    }
  },
  // Dev Items endpoints
  async getDevItems(params = { skip: 0, limit: 1000 }) {
    try {
      const response = await apiInstance.get('/api/v1/dev-items/', { params })
      return response.data
    } catch (error) {
      handleApiError(error, 'getDevItems')
    }
  },

  async createDevItem(data) {
    try {
      const response = await apiInstance.post('/api/v1/dev-items/', data)
      return response.data
    } catch (error) {
      handleApiError(error, 'createDevItem')
    }
  },

  async getDependencies() {
    try {
      const response = await apiInstance.get('/api/v1/dev-items/dependencies')
      return response.data
    } catch (error) {
      handleApiError(error, 'getDependencies')
    }
  },

  async getNetwork() {
    try {
      const response = await apiInstance.get('/api/v1/dev-items/network')
      return response.data
    } catch (error) {
      handleApiError(error, 'getNetwork')
    }
  },

  async getDevItemById(id) {
    try {
      const response = await apiInstance.get(`/api/v1/dev-items/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getDevItemById')
    }
  },

  async getDevItemByName(name) {
    try {
      const response = await apiInstance.get(`api/v1/dev-items/name/${name}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getDevItemByName')
    }
  },

  async resolveDevItemByName(name) {
    try {
      const response = await apiInstance.get('api/v1/dev-items/resolve', {
        params: { name }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'resolveDevItemByName')
    }
  },

  // Import endpoints
  async importDevItems(file, clearExisting = true) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await apiInstance.post('api/v1/csv-import/dev-items', formData, {
        params: { clear_existing: clearExisting },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'importDevItems')
    }
  },

  async getImportStatus(importId) {
    try {
      const response = await apiInstance.get(`api/v1/csv-import/status/${importId}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getImportStatus')
    }
  },

  async exportDevItems() {
    try {
      const response = await apiInstance.get('api/v1/csv-import/export/dev-items', {
        responseType: 'blob'
      })
      return response.data // Blob
    } catch (error) {
      handleApiError(error, 'exportDevItems')
    }
  },

  // Systems CSV import / export
  async importSystems(file, clearExisting = true) {
    console.log('Value of clearExisting received in api.js importSystems:', clearExisting);
    try {
      const formData = new FormData()
      formData.append('file', file)
      // DO NOT append clearExisting to formData for query parameter usage
      // formData.append('clear_existing', clearExisting)

      const res = await apiInstance.post('/api/v1/csv-import/systems', formData, {
        params: { clear_existing: clearExisting }, // Send as a URL query parameter
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return res.data
    } catch (e) {
      handleApiError(e, 'importSystems')
    }
  },

  async exportSystems() {
    try {
      const res = await apiInstance.get('/api/v1/csv-import/export/systems', {
        responseType: 'blob'
      })
      return res.data
    } catch (e) {
      handleApiError(e, 'exportSystems')
    }
  },

  // Subsystems CSV import / export
  async importSubsystems(file, clearExisting = true) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clear_existing', clearExisting)

      const res = await apiInstance.post('/api/v1/csv-import/subsystems', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return res.data
    } catch (e) {
      handleApiError(e, 'importSubsystems')
    }
  },

  async exportSubsystems() {
    try {
      const res = await apiInstance.get('/api/v1/csv-import/export/subsystems', {
        responseType: 'blob'
      })
      return res.data
    } catch (e) {
      handleApiError(e, 'exportSubsystems')
    }
  },

  // Projects endpoints
  async getProjects() {
    try {
      const response = await apiInstance.get('/api/v1/projects/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getProjects')
    }
  },

  async getProject(id) {
    try {
      const response = await apiInstance.get(`/api/v1/projects/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getProject')
    }
  },

  async createProject(data) {
    try {
      const response = await apiInstance.post('/api/v1/projects/', data)
      return response.data
    } catch (error) {
      handleApiError(error, 'createProject')
    }
  },

  // Generic CRUD methods
  async get(url, config) {
    try {
      const response = await apiInstance.get(url, config)
      return response.data
    } catch (error) {
      handleApiError(error, `GET ${url}`)
    }
  },

  async post(url, data, config) {
    try {
      const response = await apiInstance.post(url, data, config)
      return response.data
    } catch (error) {
      handleApiError(error, `POST ${url}`)
    }
  },

  async put(url, data, config) {
    try {
      const response = await apiInstance.put(url, data, config)
      return response.data
    } catch (error) {
      handleApiError(error, `PUT ${url}`)
    }
  },

  async delete(url, config) {
    try {
      const response = await apiInstance.delete(url, config)
      return response.data
    } catch (error) {
      handleApiError(error, `DELETE ${url}`)
    }
  },

  // Add a test method to verify API is working
  async testConnection() {
    try {
      console.log('Testing API connection...')
      const token = localStorage.getItem('token')
      console.log('Current token:', token ? 'Present' : 'Missing')
      
      const response = await apiInstance.get('api/v1/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      console.log('API test successful:', response.data)
      return true
    } catch (error) {
      console.error('API test failed:', error)
      return false
    }
  },

  // Add risk levels endpoints
  async getRiskLevels() {
    try {
      console.log('Getting risk levels from /api/v1/risk-levels/')
      const response = await apiInstance.get('/api/v1/risk-levels/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getRiskLevels')
    }
  },

  async updateRiskLevel(id, data) {
    try {
      const response = await apiInstance.put(`/api/v1/risk-levels/${id}/`, data)
      return response.data
    } catch (error) {
      handleApiError(error, 'updateRiskLevel')
    }
  },

  async deleteRiskLevel(id) {
    try {
      const response = await apiInstance.delete(`/api/v1/risk-levels/${id}/`)
      return response.data
    } catch (error) {
      handleApiError(error, 'deleteRiskLevel')
    }
  },

  async importRiskLevels(file, clearExisting = true) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clear_existing', clearExisting)
      
      const response = await apiInstance.post('/api/v1/risk-levels/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'importRiskLevels')
    }
  },

  async exportRiskLevels() {
    try {
      const response = await apiInstance.get('/api/v1/risk-levels/export', {
        responseType: 'blob' // Important for file downloads
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'exportRiskLevels')
    }
  },

  // Confidence levels endpoints (new UI terminology)
  async getConfidenceLevels() {
    try {
      const response = await apiInstance.get('/api/v1/confidence-levels/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getConfidenceLevels')
    }
  },

  async updateConfidenceLevel(id, data) {
    try {
      const response = await apiInstance.put(`/api/v1/confidence-levels/${id}/`, data)
      return response.data
    } catch (error) {
      handleApiError(error, 'updateConfidenceLevel')
    }
  },

  async deleteConfidenceLevel(id) {
    try {
      const response = await apiInstance.delete(`/api/v1/confidence-levels/${id}/`)
      return response.data
    } catch (error) {
      handleApiError(error, 'deleteConfidenceLevel')
    }
  },

  async importConfidenceLevels(file, clearExisting = true) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clear_existing', clearExisting)

      const response = await apiInstance.post('/api/v1/confidence-levels/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'importConfidenceLevels')
    }
  },

  async exportConfidenceLevels() {
    try {
      const response = await apiInstance.get('/api/v1/confidence-levels/export', { responseType: 'blob' })
      return response.data
    } catch (error) {
      handleApiError(error, 'exportConfidenceLevels')
    }
  },

  // User profile methods
  async updateUser(userData) {
    try {
      const response = await apiInstance.put('/api/v1/users/me/', userData)
      return response.data
    } catch (error) {
      handleApiError(error, 'updateUser')
    }
  },

  async updatePassword(passwordData) {
    try {
      const response = await apiInstance.post('/api/v1/users/me/password/', passwordData)
      return response.data
    } catch (error) {
      handleApiError(error, 'updatePassword')
    }
  },

  // Add user assignments endpoint
  async getUserAssignments(userId) {
    try {
      const response = await apiInstance.get(`/api/v1/assignments/user/${userId}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getUserAssignments')
    }
  },

  async getAllUserAssignments() {
    try {
      const response = await apiInstance.get('/api/v1/assignments/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getAllUserAssignments')
    }
  },

  async updateDisplayOrder(updates) {
    try {
      const response = await this.put('api/v1/dev-items/display-order', { updates })
      return response.data
    } catch (error) {
      console.error('Failed to update display order:', error)
      throw error
    }
  },

  // Add maturity levels endpoints
  async getMaturityLevels() {
    try {
      console.log('Getting maturity levels from /api/v1/maturity-levels/')
      const response = await apiInstance.get('/api/v1/maturity-levels/')
      return response.data
    } catch (error) {
      handleApiError(error, 'getMaturityLevels')
    }
  },

  async updateMaturityLevel(id, data) {
    try {
      const response = await apiInstance.put(`/api/v1/maturity-levels/${id}/`, data)
      return response.data
    } catch (error) {
      handleApiError(error, 'updateMaturityLevel')
    }
  },

  async deleteMaturityLevel(id) {
    try {
      const response = await apiInstance.delete(`/api/v1/maturity-levels/${id}/`)
      return response.data
    } catch (error) {
      handleApiError(error, 'deleteMaturityLevel')
    }
  },

  async importMaturityLevels(file, clearExisting = true) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clear_existing', clearExisting)

      const response = await apiInstance.post('/api/v1/maturity-levels/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'importMaturityLevels')
    }
  },

  async exportMaturityLevels() {
    try {
      const response = await apiInstance.get('/api/v1/maturity-levels/export', {
        responseType: 'blob' // Important for file downloads
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'exportMaturityLevels')
    }
  },

  async getDevItemAssignments(id) {
    try {
      console.log('Fetching assignments from:', `/api/v1/assignments/item/${id}`);
      const response = await apiInstance.get(`/api/v1/assignments/item/${id}`);
      console.log('Raw API response for assignments:', response);
      return response.data;
    } catch (error) {
      console.error('Error in getDevItemAssignments:', error);
      handleApiError(error, 'getDevItemAssignments');
    }
  },

  async getDevItemSystems(id) {
    try {
      const response = await apiInstance.get(`/api/v1/systems/item/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getDevItemSystems')
    }
  },

  async getDevItemSubsystems(id) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/item/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getDevItemSubsystems')
    }
  },

  async createSystem(data) {
    try {
      const res = await apiInstance.post('/api/v1/systems/', data)
      return res.data
    } catch (error) {
      handleApiError(error, 'createSystem')
    }
  },

  async createSubsystem(data) {
    try {
      const res = await apiInstance.post('/api/v1/subsystems/', data)
      return res.data
    } catch (error) {
      handleApiError(error, 'createSubsystem')
    }
  },

  async deleteSystem(id) {
    try {
      await apiInstance.delete(`/api/v1/systems/${id}`)
    } catch (error) {
      handleApiError(error, 'deleteSystem')
    }
  },

  async deleteSubsystem(id) {
    try {
      await apiInstance.delete(`/api/v1/subsystems/${id}`)
    } catch (error) {
      handleApiError(error, 'deleteSubsystem')
    }
  },

  async deleteDevItem(id) {
    try {
      const response = await apiInstance.delete(`/api/v1/dev-items/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'deleteDevItem')
    }
  },

  async getSystemItems(id) {
    try { const res = await apiInstance.get(`/api/v1/systems/${id}/items`) ;return res.data } catch(e){handleApiError(e,'getSystemItems')}
  },
  async addDevItemToSystem(systemId,itemId){try{await apiInstance.post(`/api/v1/systems/${systemId}/item/${itemId}`)}catch(e){handleApiError(e,'addDevItemToSystem')}},
  async removeDevItemFromSystem(systemId,itemId){try{await apiInstance.delete(`/api/v1/systems/${systemId}/item/${itemId}`)}catch(e){handleApiError(e,'removeDevItemFromSystem')}},
  async getSubsystemItems(id){try{return (await apiInstance.get(`/api/v1/subsystems/${id}/items`)).data}catch(e){handleApiError(e,'getSubsystemItems')}},
  async addDevItemToSubsystem(subId,itemId){try{await apiInstance.post(`/api/v1/subsystems/${subId}/item/${itemId}`)}catch(e){handleApiError(e,'addDevItemToSubsystem')}},
  async removeDevItemFromSubsystem(subId,itemId){try{await apiInstance.delete(`/api/v1/subsystems/${subId}/item/${itemId}`)}catch(e){handleApiError(e,'removeDevItemFromSubsystem')}},
  // Subsystem links
  async listSubsystemAggregationParents(id){
    try { const res = await apiInstance.get(`/api/v1/subsystems/${id}/links/aggregation`); return res.data } catch(e){ handleApiError(e,'listSubsystemAggregationParents') }
  },
  async listSubsystemAggregationChildren(id){
    try { const res = await apiInstance.get(`/api/v1/subsystems/${id}/links/aggregation/children`); return res.data } catch(e){ handleApiError(e,'listSubsystemAggregationChildren') }
  },
  async addSubsystemAggregationLink(sourceId,targetId){
    try { await apiInstance.post(`/api/v1/subsystems/${sourceId}/links/aggregation`, { source_id: sourceId, target_id: targetId }) } catch(e){ handleApiError(e,'addSubsystemAggregationLink') }
  },
  async removeSubsystemAggregationLink(sourceId,targetId){
    try { await apiInstance.delete(`/api/v1/subsystems/${sourceId}/links/aggregation`, { data: { source_id: sourceId, target_id: targetId } }) } catch(e){ handleApiError(e,'removeSubsystemAggregationLink') }
  },
  async listSubsystemDependencies(id){
    try { const res = await apiInstance.get(`/api/v1/subsystems/${id}/links/dependency`); return res.data } catch(e){ handleApiError(e,'listSubsystemDependencies') }
  },
  async listSubsystemDependents(id){
    try { const res = await apiInstance.get(`/api/v1/subsystems/${id}/links/dependency/dependents`); return res.data } catch(e){ handleApiError(e,'listSubsystemDependents') }
  },
  async addSubsystemDependencyLink(sourceId,targetId){
    try { await apiInstance.post(`/api/v1/subsystems/${sourceId}/links/dependency`, { source_id: sourceId, target_id: targetId }) } catch(e){ handleApiError(e,'addSubsystemDependencyLink') }
  },
  async removeSubsystemDependencyLink(sourceId,targetId){
    try { await apiInstance.delete(`/api/v1/subsystems/${sourceId}/links/dependency`, { data: { source_id: sourceId, target_id: targetId } }) } catch(e){ handleApiError(e,'removeSubsystemDependencyLink') }
  },
  async recalcSystemLevels(id) {
    try {
      const response = await apiInstance.post(`/api/v1/systems/${id}/recalculate-levels`)
      return response.data
    } catch (error) {
      handleApiError(error, 'recalcSystemLevels')
    }
  },
  async recalcSubsystemLevels(id) {
    try {
      const response = await apiInstance.post(`/api/v1/subsystems/${id}/recalculate-levels`)
      return response.data
    } catch (error) {
      handleApiError(error, 'recalcSubsystemLevels')
    }
  },

  async getTreeStats() {
    try {
      const response = await apiInstance.get('/api/v1/subsystems/tree-stats')
      return response.data
    } catch (error) {
      handleApiError(error, 'getTreeStats')
    }
  },

  async getSubsystemStatsDetail(subsystemId) {
    try {
      const response = await apiInstance.get(`/api/v1/subsystems/${subsystemId}/stats-detail`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getSubsystemStatsDetail')
    }
  },

  async recalculateProjectTree() {
    try {
      const response = await apiInstance.post('/api/v1/subsystems/recalculate-project')
      return response.data
    } catch (error) {
      handleApiError(error, 'recalculateProjectTree')
    }
  },

  // Add deployment info endpoint
  async getDeploymentInfo() {
    try {
      const response = await apiInstance.get('/api/v1/debug/info')
      return response.data
    } catch (error) {
      handleApiError(error, 'getDeploymentInfo')
    }
  },

  // Actions endpoints
  async getActions(params = {}) {
    try {
      const response = await apiInstance.get('/api/v1/actions/', { params })
      return response.data
    } catch (error) {
      handleApiError(error, 'getActions')
    }
  },

  async getActionById(id) {
    try {
      const response = await apiInstance.get(`/api/v1/actions/${id}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getActionById')
    }
  },

  async createAction(data) {
    try {
      const response = await apiInstance.post('/api/v1/actions/', data)
      return response.data
    } catch (error) {
      handleApiError(error, 'createAction')
    }
  },

  async updateAction(id, data) {
    try {
      const response = await apiInstance.put(`/api/v1/actions/${id}`, data)
      return response.data
    } catch (error) {
      handleApiError(error, 'updateAction')
    }
  },

  async getActionByName(name) {
    try {
      const response = await apiInstance.get(`/api/v1/actions/name/${encodeURIComponent(name)}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getActionByName')
    }
  },

  async completeAction(id) {
    try {
      const response = await apiInstance.post(`/api/v1/actions/${id}/complete`)
      return response.data
    } catch (error) {
      handleApiError(error, 'completeAction')
    }
  },

  async deleteAction(id) {
    try {
      await apiInstance.delete(`/api/v1/actions/${id}`)
    } catch (error) {
      handleApiError(error, 'deleteAction')
    }
  },

  async getActionHistory(id) {
    try {
      const response = await apiInstance.get(`/api/v1/actions/${id}/history`)
      return response.data
    } catch (error) {
      handleApiError(error, 'getActionHistory')
    }
  },

  async getActionStats() {
    try {
      const response = await apiInstance.get('/api/v1/actions/stats')
      return response.data
    } catch (error) {
      handleApiError(error, 'getActionStats')
    }
  },

  // Retro (Landed) endpoint
  async getLanded({ start, end, user = 'all', category = 'all' }) {
    try {
      const response = await apiInstance.get('/api/v1/retro/landed', {
        params: { start, end, user, category }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'getLanded')
    }
  },
  
  // Actions import/export
  async importActions(file, clearExisting = true) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('clear_existing', clearExisting)
    
    try {
      const response = await apiInstance.post('/api/v1/csv-import/actions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'importActions')
    }
  },
  
  async exportActions() {
    try {
      const response = await apiInstance.get('/api/v1/csv-import/export/actions', {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'exportActions')
    }
  },

  // YouTrack Integration
  async getYouTrackConfig() {
    try {
      const response = await apiInstance.get('/api/v1/youtrack/config')
      return response.data
    } catch (error) {
      handleApiError(error, 'getYouTrackConfig')
    }
  },

  async saveYouTrackConfig(config) {
    try {
      const response = await apiInstance.post('/api/v1/youtrack/config', config)
      return response.data
    } catch (error) {
      handleApiError(error, 'saveYouTrackConfig')
    }
  },

  async testYouTrackConnection(config) {
    try {
      const response = await apiInstance.post('/api/v1/youtrack/test-connection', config)
      return response.data
    } catch (error) {
      handleApiError(error, 'testYouTrackConnection')
    }
  },

  async listYouTrackProjects(config) {
    try {
      const response = await apiInstance.post('/api/v1/youtrack/list-projects', config)
      return response.data
    } catch (error) {
      handleApiError(error, 'listYouTrackProjects')
    }
  },

  async clearYouTrackConfig() {
    try {
      const response = await apiInstance.delete('/api/v1/youtrack/config')
      return response.data
    } catch (error) {
      handleApiError(error, 'clearYouTrackConfig')
    }
  },

  async createYouTrackIssue(issueData) {
    try {
      const response = await apiInstance.post('/api/v1/youtrack/issues/create', issueData)
      return response.data
    } catch (error) {
      handleApiError(error, 'createYouTrackIssue')
    }
  },

  async syncYouTrackIssue(actionId) {
    try {
      const response = await apiInstance.post(`/api/v1/youtrack/issues/${actionId}/sync`)
      return response.data
    } catch (error) {
      handleApiError(error, 'syncYouTrackIssue')
    }
  },

  async deleteYouTrackIssue(actionId) {
    try {
      const response = await apiInstance.delete(`/api/v1/youtrack/issues/${actionId}`)
      return response.data
    } catch (error) {
      handleApiError(error, 'deleteYouTrackIssue')
    }
  },

  async unlinkYouTrackIssue(actionId) {
    try {
      const response = await apiInstance.delete(`/api/v1/youtrack/issues/${actionId}/unlink`)
      return response.data
    } catch (error) {
      handleApiError(error, 'unlinkYouTrackIssue')
    }
  },

  async linkYouTrackIssue(actionId, issueKey) {
    try {
      const response = await apiInstance.post(`/api/v1/youtrack/issues/${actionId}/link`, null, {
        params: { issue_key: issueKey }
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'linkYouTrackIssue')
    }
  },

  async debugYouTrackCustomFields() {
    try {
      const response = await apiInstance.get('/api/v1/youtrack/debug/custom-fields')
      return response.data
    } catch (error) {
      handleApiError(error, 'debugYouTrackCustomFields')
    }
  },

  async debugYouTrackSyncStatus() {
    try {
      const response = await apiInstance.get('/api/v1/youtrack/debug/sync-status')
      return response.data
    } catch (error) {
      handleApiError(error, 'debugYouTrackSyncStatus')
    }
  },

  async debugYouTrackWebhookStatus() {
    try {
      const response = await apiInstance.get('/api/v1/youtrack/debug/webhook-status')
      return response.data
    } catch (error) {
      handleApiError(error, 'debugYouTrackWebhookStatus')
    }
  },

  async testYouTrackWebhook() {
    try {
      const response = await apiInstance.post('/api/v1/youtrack/debug/test-webhook', {
        test: 'webhook test data'
      })
      return response.data
    } catch (error) {
      handleApiError(error, 'testYouTrackWebhook')
    }
  }
}